/**
 * Cloudflare Turnstile Widget
 * Note: Requires @cloudflare/turnstile-react package
 */
import React, { useEffect, useRef } from 'react';

interface TurnstileWidgetProps {
  onVerify: (token: string) => void;
  onError?: (error: string) => void;
}

export function TurnstileWidget({ onVerify, onError }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    // Wait for Turnstile script to load
    const loadTurnstile = () => {
      if (!window.turnstile || !containerRef.current) {
        setTimeout(loadTurnstile, 100);
        return;
      }

      const sitekey = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

      // Render widget
      widgetId.current = window.turnstile.render(containerRef.current, {
        sitekey,
        callback: onVerify,
        'error-callback': onError,
        theme: 'light',
        size: 'normal',
      });
    };

    loadTurnstile();

    // Cleanup
    return () => {
      if (widgetId.current && window.turnstile) {
        window.turnstile.remove(widgetId.current);
      }
    };
  }, [onVerify, onError]);

  return <div ref={containerRef} className="flex justify-center my-4" />;
}

// Load Turnstile script
if (typeof window !== 'undefined' && !document.getElementById('turnstile-script')) {
  const script = document.createElement('script');
  script.id = 'turnstile-script';
  script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
  script.async = true;
  document.head.appendChild(script);
}

// Extend Window interface
declare global {
  interface Window {
    turnstile: {
      render: (container: HTMLElement, options: any) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId: string) => void;
    };
  }
}
